/**
 * ConfidencePill — score display for 0–1 confidence values.
 *
 * Default variant renders a slim bar + score + tone label ("0.95 · High").
 * `compact` drops the bar for dense table cells.
 *
 * Tone bands:
 *   ≥ 0.90  → "High"  (accent / green tint)
 *   ≥ 0.70  → "Med"   (accent / amber tint)
 *   < 0.70  → "Low"   (muted)
 *
 * Replaces the inline ConfidenceBar in SpinoutDetailPage and gives Radar
 * table cells a consistent affordance.
 */
import styles from './ConfidencePill.module.css';

export interface ConfidencePillProps {
  /** 0–1 score; values outside the range are clamped. */
  value: number;
  /** Compact rendering (no bar) for dense table cells. */
  compact?: boolean;
}

type Tone = 'high' | 'med' | 'low';

function toneFor(v: number): Tone {
  if (v >= 0.9) return 'high';
  if (v >= 0.7) return 'med';
  return 'low';
}

const TONE_LABEL: Record<Tone, string> = {
  high: 'High',
  med: 'Med',
  low: 'Low',
};

export function ConfidencePill({ value, compact = false }: ConfidencePillProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const tone = toneFor(clamped);
  const pct = clamped * 100;

  if (compact) {
    return (
      <span
        className={`${styles.compact} ${styles[`tone_${tone}`]}`}
        title={`Confidence ${clamped.toFixed(2)} (${TONE_LABEL[tone]})`}
      >
        <span className={styles.score}>{clamped.toFixed(2)}</span>
        <span className={styles.tone}>{TONE_LABEL[tone]}</span>
      </span>
    );
  }

  return (
    <span className={`${styles.full} ${styles[`tone_${tone}`]}`} title={`Confidence ${clamped.toFixed(2)}`}>
      <span className={styles.track}>
        <span className={styles.fill} style={{ width: `${pct}%` }} />
      </span>
      <span className={styles.score}>{clamped.toFixed(2)}</span>
      <span className={styles.tone}>· {TONE_LABEL[tone]}</span>
    </span>
  );
}
